import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Sidebar, { SidebarProvider } from '../../components/navigation/Sidebar';
import { useModal } from '../../components/ui/ModalProvider';
import { getAccountUsage } from '../../services/planService';
import { getInitials } from '../../utils/avatar';
import ProfileCard from './settings/ProfileCard';
import ProviderCredentialCard from './settings/ProviderCredentialCard';
import AIProviderSection from './settings/AIProviderSection';
import MidjourneyCard from './settings/MidjourneyCard';
import AccountUsageCard from './settings/AccountUsageCard';

const IMAGE_PROVIDERS = [
  { value: 'openai', label: 'OpenAI (DALL·E)' },
  { value: 'stability', label: 'Stability AI' },
  { value: 'replicate', label: 'Replicate' },
];

export default function Settings() {
  const { user, userProfile, isManager, updateProfile } = useAuth();
  const { alert, confirm } = useModal();
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setUsageLoading(true);
      const { data } = await getAccountUsage();
      if (active) {
        setUsage(data);
        setUsageLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user?.id]);

  return (
    <SidebarProvider>
      <div className="min-h-screen bg-gray-50">
        <Sidebar />
        <main className="main-content">
          <div className="max-w-7xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Inter, Poppins, sans-serif' }}>
                Settings
              </h1>
              <p className="text-gray-600 mt-2">Manage your profile, account and personal integrations.</p>

              <div className="mt-5 flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 shadow-sm w-fit">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-sm font-bold overflow-hidden flex-shrink-0">
                  {userProfile?.avatar_url ? (
                    <img src={userProfile.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    getInitials(userProfile?.full_name, user?.email)
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{userProfile?.full_name || user?.email}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
                <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-medium capitalize">{userProfile?.role || 'user'}</span>
                <span className="text-xs px-2.5 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                  {usage?.is_unlimited ? 'Unlimited' : 'Trial'}
                </span>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-6">
                <ProfileCard
                  user={user}
                  userProfile={userProfile}
                  usage={usage}
                  updateProfile={updateProfile}
                  alert={alert}
                />
              </div>

              <div className="space-y-6">
                <AIProviderSection alert={alert} confirm={confirm} />
                <ProviderCredentialCard
                  title="Image Provider"
                  description="Connect your own officially supported image-generation provider using your own API key."
                  capability="image"
                  providers={IMAGE_PROVIDERS}
                  helperNote="Used for AI-generated images outside of MidJourney — a separate connection from your MidJourney setup below."
                  alert={alert}
                  confirm={confirm}
                />
                <MidjourneyCard isManager={isManager} alert={alert} confirm={confirm} />
              </div>
            </div>

            <div className="mt-6">
              <AccountUsageCard usage={usage} loading={usageLoading} />
            </div>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}
